import { ICountry, IState } from './country/models'
import type { TShowForRouteArray } from './fieldSet/models'
import type {
  IAddressLineSegment,
  TAddressVerificationStatus,
} from './smarty/models'

// Entity Mask

export const entityMaskDataTypes = [
  'textField',
  'textArea',
  'select',
  // A kit's optional items: the picks as chips plus a "Choose Options..."
  // button opening them one select per choice in their own window. Holds the
  // same flat value a `multiple` select would. See OptionsChooserField.vue.
  'optionsChooser',
  'autocomplete',
  'combobox',
  'checkbox',
  'dateField',
  'timeField',
  'slider',
  'numberTextField',
  'currencyTextField',
  'percentageTextField',
  'dateTextField',
  'dateTimeDisplayDateOnlyTextField',
  'dateTimeDisplayTimeOnlyTextField',
  'phoneNumberTextField',
  'fileSelect',
  // Display-only, like 'subtitle': renders the USPS verification panel under the
  // address block. Carries no value and is never part of the save payload.
  'addressVerification',
  // Display-only, like 'addressVerification': renders the duplicate-person
  // "did you mean…?" panel on the person create form. Carries no value; the
  // identity fields' runOnBlur fill it. See cynqueModules/personMatch.ts (#113).
  'personMatchPanel',
] as const

export type TEntityMaskDataTypes = (typeof entityMaskDataTypes)[number]

export type TEntityMaskDataValueTypes =
  | string
  | boolean
  | number
  | object
  | object[]
  | undefined
  | null

export interface IEntityMaskData {
  name: string
  // The object path, relative to the entity, the field's value is wrapped in.
  // May be a dot-path of arbitrary depth (e.g. 'address' or
  // 'supplierProduct.product'); the value is read from / written to
  // wrapperObjectName + name, navigated and rebuilt level by level with sibling
  // properties preserved at each level. See cynqueModules/wrapperObjectPath.ts.
  wrapperObjectName?: string
  label?: string
  type: TEntityMaskDataTypes
  accept?: string // This is for fileSelect. It's the same as an <input accept/> string
  clearable?: boolean
  doReturnObject?: boolean
  // The NAME of the item property to store and to match on (e.g. 'email',
  // '_selectUid'), not a value from `items`. Defaults to 'id'. Mirrors Vuetify's
  // `item-value`.
  itemValue?: string
  // Edited as a LIST (a `multiple` combobox), stored as ONE STRING joined with
  // this separator (e.g. ', '). Split on open, joined on save; see
  // cynqueModules/componentHelpers/entityMaskForm/delimitedList.ts.
  delimitedStringSeparator?: string
  items?: object[] | string[]
  setSelectItems?: Function
  icon?: string
  min?: number
  max?: number
  default?: TEntityMaskDataValueTypes
  cols?: number
  class?: string
  highlight?: boolean
  multiple?: boolean
  chips?: boolean
  smallChips?: boolean
  allowSelectAll?: boolean
  selectAllEnabled?: Function
  forceSingleSelectWhen?: Function
  subentity?: string
  subentityNameTitleOverride?: string
  subentityInputFieldsEntityNameOverride?: string
  subentityAfterRequestProcessor?: Function
  subentityParentObjectForCreate?: Function
  namesOfSubentityFieldsWhoseValuesToSetFromEntityFields?: string[]
  valuesToPassToSubentityDialog?: IIFieldValueForFieldName[]
  allowCreateNewSubentityWhenEditing?: boolean
  hint?: string
  // Override the app-wide field variant ('underlined', set as a component
  // default in nuxt.config.ts) for this one field. textArea only, for now.
  variant?: 'underlined' | 'outlined' | 'filled' | 'plain' | 'solo'
  // A textArea's height in lines (its starting height when it auto-grows).
  // Default 1.
  rows?: number
  // false: a textArea keeps `rows` and scrolls, with the browser's corner grip
  // to drag it taller. Default true, which grows with the text instead --
  // Vuetify hides the grip whenever auto-grow is on (`v-textarea--no-resize`).
  autoGrow?: boolean
  // Show `hint` all the time rather than only while focused. Select only.
  persistentHint?: boolean
  noDataText?: string
  validation?: IEntityFieldValidation
  showForRoutes?: TShowForRouteArray
  showPicker?: boolean
  readonly?: boolean
  disableBasedOnState?: TCreatingEditing
  // Like disableBasedOnState, but the field stays fully legible: set readonly
  // (not disabled) while creating / editing. Applied by the same
  // setInputFieldsState pass, at open.
  readonlyBasedOnState?: TCreatingEditing
  getStateForDisabled?: Function
  // Like getStateForDisabled, for `readonly`: re-run after every field change.
  getStateForReadonly?: Function
  clearOnDisable?: boolean
  disabled?: boolean
  unrenderBasedOnState?: TCreatingEditing
  getStateForUnrendered?: Function
  clearOnUnrender?: boolean
  ignoreIfUnrendered?: boolean // Used when we need two fields for the same property, but only one will be rendered and we want to ignore the other one for setting isDirty.
  unrendered?: boolean
  tiedToPriorFieldCheckbox?: 'true'
  runOnOpen?: Function
  runOnChanged?: Function
  runOnChangedTargetField?: string
  runOnBlur?: Function
  runOnDefaultSet?: Function
  // Fired as the user TYPES, from @update:search, debounced by the form. This is
  // the seam for a remotely-searched item list: unlike setSelectItems (which
  // loads the whole list once, at form init and on focus), runOnSearch is handed
  // the current search text and is expected to set `items` itself.
  // Called as runOnSearch({ searchText, ifield, inputFields,
  // entityParentObjectForCreate, that }) -- the object-argument form, matching
  // runOnDefaultSet rather than runOnChanged's positional one.
  // Only the combobox widget wires it up today.
  runOnSearch?: Function
  runOnSearchDebounceMs?: number // default 250
  // Binds :no-filter. Set it whenever `items` is already ranked by a server for
  // the current search text, so Vuetify does not filter the list a second time
  // against its own (possibly stale) notion of the query.
  doNotFilterItems?: boolean
  // Binds :loading. Set by whatever populates `items` asynchronously.
  isLoadingItems?: boolean
  clearOtherOnChanged?: string[]
  clearOtherOnChangedIfNotIsEditing?: string[]
  clearOtherItemsOnChanged?: string[]
  clearOtherOnCleared?: string[]
  isForDisplayOnly?: 'true'
  doAlwaysTreatAsDirty?: 'true'
  doAlwaysTreatAsDirtyWhenEditing?: 'true' // Used to include unchanged data, such as version, in the updatedData object
  // When true, the field is never treated as dirty: excluded from the saved
  // POST/PATCH payload AND from whole-form dirty detection, even if its value
  // changed. For create-flow filter/cascade fields whose values drive other
  // selects but must never be persisted (e.g. productCategory/productSubcategory
  // above productGroup).
  neverTreatAsDirty?: boolean
  // Fields saved WITH this one: whenever this field is dirty, the named fields
  // go in the payload too, at their current values, dirty or not. For a pair
  // the server validates together -- a case's `company` and `companyMSService`,
  // exactly one of which may be set -- so a change to either always sends both.
  // It adds to the payload only; it does not make the form dirty.
  sendWith?: string[]
  // Never the field the dialog focuses on open (focusOnFirstField), e.g. a
  // checkbox that only reveals other fields.
  skipAutoFocus?: boolean
  // The field the dialog focuses on open even when it already has text, with
  // the caret at the very start: preset text the user writes ABOVE, such as a
  // reply's signature. Fields before it still come first.
  autoFocusAtStart?: boolean
  // Names the dialog's Save button from the form's live values; return
  // undefined to defer. Beats the caller's saveButtonCaptionVerbOverride. See
  // cynqueModules/componentHelpers/entityMaskForm/saveButtonLabel.ts.
  getSaveButtonLabel?: (params: {
    inputFields: IEntityMaskData[]
    isEditing: boolean
  }) => string | undefined
  no1PasswordInteraction?: 'true' // Prevents 1password from showing an icon in the field
  setValueForDisplayOnly?: ISetValueForDisplayOnlyFunction
  //
  // type: 'addressVerification' only. The panel is display-only and holds no
  // value; everything it renders is written onto the field by the address
  // spec's blur handler, and `unrendered` is what hides it when there is
  // nothing to say. See cynqueModules/addressVerification.ts.
  //
  verificationStatus?: TAddressVerificationStatus
  verificationMessage?: string
  /** The standardized address: one entry per line of a mailing address, each a
   *  list of segments flagged for whether they differ from what was typed.
   *  Empty = nothing to offer, which is also what hides "Use Change". */
  verificationSuggestionLines?: IAddressLineSegment[][]
  /** Invoked by the panel's "Use Change" button. Owned by the address spec so the
   *  form stays generic. Called as (ifield, inputFields). */
  applyVerificationSuggestion?: Function
  /** Invoked by the panel's "Keep Mine" button, which declines the suggestion.
   *  Called as (ifield, inputFields). */
  dismissVerification?: Function
  /** Fingerprint of the address the user dismissed the panel for, so that a
   *  re-blur does not immediately re-raise it. Cleared implicitly: any edit
   *  changes the fingerprint. See cynqueModules/addressVerification.ts. */
  dismissedAddressSignature?: string
  value: TEntityMaskDataValueTypes
  start: TEntityMaskDataValueTypes
  //
  // Not used in input specs. Sometimes set in EntityMaskForm.
  //
  ignoreClearOnChangedRequests?: true
  fieldValidations?: Function[]
  ignoreSetStateRequests?: boolean
  //
  [propName: string]: any
}

type TCreatingEditing = 'CREATING' | 'EDITING'
type IIFieldName = string

export interface IEntityFieldValidation {
  required?: true
  minLength?: number
  // Max size in bytes for a `fileSelect` field. See cynqueModules/uploadLimits.ts
  // for where the number comes from; the rule is built in fieldValidations.ts.
  maxBytes?: number
  email?: true
  kitQuantity?: true
  // An msService's `kitQuantity`: the kitQuantity grammar on a row inside a
  // kit, a whole number on any other row (a stand-alone item). Reads the row
  // from EntityMaskForm's entityData.
  msServiceQuantity?: true
  tableHeaderWidth?: true
  ipAddress?: true
  subnetMask?: true
  url?: true
  labelForFieldName?: true
  sameOrGreaterThanISODateStringInIField?: IIFieldName
  onePerSection?: true
  // A rule of the spec's own, which may read the other fields -- e.g. an
  // address repeated across To / cc / bcc. Returns true, or the message.
  custom?: (value: any, params: IDoValidateIf) => true | string
  doValidateIfFunction?: IDoValidateIfFunction // gate: this object's rules apply only when this returns true
}

export interface IDoValidateIf {
  inputFields?: IEntityMaskData[]
  entityParentObjectForCreate?: IEntityParentObjectForCreate
}

interface IDoValidateIfFunction {
  (params: IDoValidateIf): boolean
}

interface ISetValueForDisplayOnlyFunction {
  (params: ISetValueForDisplayOnly): void
}

export interface ISetValueForDisplayOnly {
  thisField: IEntityMaskData
  inputFields: IEntityMaskData[]
  isSubentityDialog: boolean
  that: any
}

interface IEntityMaskWidget {
  name: string
  label: string
  type: 'subtitle' | 'divider' | 'spacer'
  cols: number
  unrendered?: boolean
  highlight?: boolean
  showForRoutes?: TShowForRouteArray
  getStateForUnrendered?: Function
  unrenderBasedOnState?: TCreatingEditing
  disableBasedOnState?: TCreatingEditing
  [propName: string]: any
}

export interface ISpacer extends IEntityMaskWidget {
  type: 'spacer'
}

interface IEntityMaskWidgetWithClass extends IEntityMaskWidget {
  class?: string
}

export interface ISubtitle extends IEntityMaskWidgetWithClass {
  type: 'subtitle'
}

export interface IDivider extends IEntityMaskWidgetWithClass {
  type: 'divider'
}

// A tab marker. It renders nothing itself: every item AFTER it, up to the next
// marker, belongs to a tab headed `label`, and items before the first marker
// stay visible above the tab bar. The array stays flat on purpose -- a field's
// index is its identity inside EntityMaskForm (rules, refs, debouncers,
// tiedToPriorFieldCheckbox) -- so tabs are a presentation layer only. See
// cynqueModules/componentHelpers/entityMaskForm/tabGroups.ts.
export interface ITab {
  name: string
  label: string
  type: 'tab'
  showForRoutes?: TShowForRouteArray
  [propName: string]: any
}

export type TEntityMaskItem =
  | IEntityMaskData
  | IDivider
  | ISubtitle
  | ISpacer
  | ITab

export function isTab(entityMaskItem: TEntityMaskItem): entityMaskItem is ITab {
  return entityMaskItem.type === 'tab'
}

export function isEntityMaskData(
  entityMaskItem: TEntityMaskItem
): entityMaskItem is IEntityMaskData {
  return entityMaskDataTypes.includes((entityMaskItem as IEntityMaskData).type)
}

export function isSpacer(
  entityMaskItem: TEntityMaskItem
): entityMaskItem is ISpacer {
  return (
    (entityMaskItem as ISpacer).hasOwnProperty('type') &&
    (entityMaskItem as ISpacer).type === 'spacer'
  )
}

export function isDivider(
  entityMaskItem: TEntityMaskItem
): entityMaskItem is IDivider {
  return (
    (entityMaskItem as IDivider).hasOwnProperty('type') &&
    (entityMaskItem as IDivider).type === 'divider'
  )
}

// List Cards

export interface IColumnHeader {
  text: string
  align: 'left' | 'center' | 'right'
  width: string
  value: string
  formatter?(arg0: any): string
  sortable?: boolean
  dependsOn?: Function
  class?: string
  cellClass?: string
  clickable?: boolean
  clickRoute?: string
  clickIdPath?: string
  /**
   * Does this column apply to this row? TableCard blanks the cell of a row that
   * fails it; `dropColumnsUnusedByItems` drops the whole column from a table in
   * which no row passes.
   */
  showIf?(item: any): boolean
  /**
   * The cell's value -- shown, sorted and searched -- computed from the row,
   * for a column whose stored field is not what it should display (the Term
   * column shows a majority term where the stored term is 'Varies'). `value`
   * still names the column: slot, sort key and saved sort.
   */
  displayValue?(item: any): unknown
  /** With `hoverable`: only rows that pass get the hover; the rest are plain text. */
  hoverableIf?(item: any): boolean
  /** With `hoverable`: the text of a cell that gets the hover, in place of `formatter`. */
  hoverableFormatter?(value: any): string
  /**
   * Only treat this column's cells as clickable while Option/Alt is held.
   *
   * For a cell whose navigation target differs from its ROW's target. The
   * Company column on both Opportunities cards is the case this was built for:
   * the row goes to the opportunity overview, the cell went to the company
   * overview, and the two are a few pixels apart — so people aiming for the
   * opportunity kept landing on the company. Gating on a modifier keeps the
   * shortcut for whoever wants it while making it impossible to trigger by
   * accident.
   *
   * The affordance follows the gate: with Option up the cell is plain text,
   * with it down the cell takes the `clickable-cell` styling. Nothing is
   * clickable-looking that is not clickable.
   *
   * ⚠️ Undiscoverable by design — a user who does not know about it will never
   * find it. Fine for a secondary shortcut; do NOT gate a column that is the
   * only way to reach something.
   */
  clickRequiresAltKey?: boolean
  /**
   * This column holds website addresses: each cell renders as a link opening
   * that site in a new tab. Declared per column rather than detected from the
   * data, so a column either links or it does not — `clickable` above is the
   * in-app equivalent. Combines with `formatter` (the formatter still supplies
   * the displayed text; the link is built from the raw value).
   */
  webAddress?: boolean
  /**
   * This column's cells are an ICON, not text: the cell value is an mdi name
   * (e.g. 'mdi-email-outline') and is rendered as a <v-icon>.
   *
   * For a per-row TYPE marker, where a word would cost a column's width to say
   * what a glyph says at a glance. The value is per row, so one column can show
   * a different icon on each -- which is the whole point; a fixed icon would
   * belong in the header.
   *
   * ⚠ Do not combine with `formatter`: the formatter branch is skipped for these
   * columns, so the formatter would silently never run.
   */
  showAsIcon?: boolean
  clickMenuItems?: (item: any) => Array<{
    title: string
    icon?: string
    action: () => void | Promise<void>
  }>
  /**
   * A non-clickable line above the `clickMenuItems` -- e.g. the address of the
   * location the cell names. Nothing renders while it returns a falsey value,
   * so it can show something fetched by `onClickMenuOpen`.
   */
  clickMenuSubheader?: (item: any) => string | null | undefined
  /**
   * An mdi icon for the `clickMenuSubheader`, set in the menu items' icon
   * column so the subheader's text lines up with their titles.
   */
  clickMenuSubheaderIcon?: string
  /**
   * Non-clickable "count: label" lines between the `clickMenuSubheader` and
   * the `clickMenuItems` -- a varied kit row's "3 services: Boston HQ". Nothing
   * renders while it returns none.
   */
  clickMenuBreakdownRows?: (item: any) => { count: string; label: string }[]
  /** Called each time the cell's `clickMenuItems` menu opens. */
  onClickMenuOpen?: (item: any) => void
  /**
   * More text the card's Search field matches this column on, beyond what it
   * displays -- e.g. the whole address behind a Location label. See
   * `rowMatchesSearchText` (cynqueModules/componentHelpers/tableCard).
   */
  searchText?: (item: any) => string | null | undefined
  clickFunction?: (
    item: any
  ) =>
    | { route: string; id?: string; query?: Record<string, string> }
    | { action: () => void | Promise<void> }
    | Promise<
        | { route: string; id?: string; query?: Record<string, string> }
        | { action: () => void | Promise<void> }
        | undefined
      >
    | undefined
}

// export interface IDialogTitleModifier {
//   condition: string
//   prefix: string
// }

export interface IListPageTopButtonBarSelectValues {
  groupBy: IListPageTopButtonBarSelectValuesObject
  showForStatus: IListPageTopButtonBarSelectValuesObject
  sortAllBy: IListPageTopButtonBarSelectValuesObject
  teamMemberFilter?: {
    cols: number
    label: string
    unassignedTitle?: string
    keys: string[]
  }
  search?: { cols: number }
}

interface IListPageTopButtonBarSelectValuesObject {
  cols: Number
  items: object[]
}

export interface IAddress {
  country: ICountry
  addressLine1: string
  addressLine2?: string
  city: string
  state: IState | null
  zip: string
  descr?: string
  shortDescr?: string
  addressLines?: string
  cityStateZip?: string
}

export interface ISelectItem {
  id: number | string
  label: string
  // Authored top-level, the Vuetify 2 shape. Vuetify 3 reads per-item props only from
  // `item.props`, so it is bridged at render time by
  // cynqueModules/componentHelpers/entityMaskForm/selectItemProps.ts rather than being
  // duplicated onto every list literal -- the app's own logic filters on this key, and
  // a `doReturnObject` field writes this very object into the request body.
  disabled?: boolean
  // Vuetify 3 keys a non-item row (divider / subheader) off `type`; V2's bare
  // `divider: true` is not read at all. See services/products.ts and suppliers.ts.
  type?: string
}

export interface IEntitySelectItem extends ISelectItem {
  id: TEntityId
  hrid?: THrid
  parent?: IEntitySelectItem
  [propName: string]: any
}

export interface IEntitySelectItemWithParent extends IEntitySelectItem {
  parent: IEntitySelectItem
}

export interface IEntitySelectItemWithOptionalAvatar extends IEntitySelectItem {
  avatar?: IAvatar
}

export interface IStateItem {
  id: string
  descr: string
}

export interface IGroupItemValue {
  id: number | string
  groupLabel: string
  itemLabel: string
}

export function isSelectItem(obj: any): obj is ISelectItem {
  return (
    typeof obj === 'object' && obj !== null && 'id' in obj && 'label' in obj
  )
}

export function isSelectItemWithEntityName(
  obj: any
): obj is ISelectItemWithEntityName {
  return isSelectItem(obj) && 'entityName' in obj
}

export function isDisabledSelectItem(obj: any): obj is ISelectItem {
  return (
    obj &&
    typeof obj === 'object' &&
    'id' in obj &&
    'label' in obj &&
    'disabled' in obj &&
    obj.disabled
  )
}

export function isEntitySelectItem(obj: any): obj is IEntitySelectItem {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'id' in obj &&
    typeof obj.id === 'string' &&
    'label' in obj
  )
}

interface Labeled {
  label: string
}
interface Ided {
  id: string | number
}

interface NumberIded {
  id: number
}

export function hasLabelProperty(obj: any): obj is Labeled {
  return obj && typeof obj === 'object' && 'label' in obj
  // typeof obj === 'object' passes null, and 'in' can't be used
  // with null, hence 'obj &&' test.
}

export function hasIdProperty(obj: any): obj is Ided {
  return obj && typeof obj === 'object' && 'id' in obj
  // typeof obj === 'object' passes null, and 'in' can't be used
  // with null, hence 'obj &&' test.
}

export function hasStringIdProperty(obj: any): obj is Ided {
  return (
    obj && typeof obj === 'object' && 'id' in obj && typeof obj.id === 'string'
  )
  // typeof obj === 'object' passes null, and 'in' can't be used
  // with null, hence 'obj &&' test.
}

export function hasNumberIdProperty(obj: any): obj is NumberIded {
  return (
    obj && typeof obj === 'object' && 'id' in obj && typeof obj.id === 'number'
  )
  // typeof obj === 'object' passes null, and 'in' can't be used
  // with null, hence 'obj &&' test.
}

export interface ISelectItemWithEntityName extends ISelectItem {
  entityName: string
}

export interface IItemWithStatus {
  status: ISelectItem
  [propName: string]: any
}

export interface IStatusCount {
  status: ISelectItem
  count: number
}

export interface IItemWithLocation {
  location: ISelectItem
  [propName: string]: any
}

export interface ILocationCount {
  location: ISelectItem
  count: number
}

export interface IIFieldValueForFieldName {
  fieldName: string
  value: string | ISelectItem | ISelectItemWithEntityName
}

export type TEntityId = string
export type THrid = string
export type TEntityVersion = string
export type TEntityName = string // we can eventually limit this to list of all entity names
export type TDisplayString = string

export interface IEntityDataItem {
  id: TEntityId
  hrid: THrid
  version?: TEntityVersion
  createdAt?: string
  modifiedAt?: string
  canBeDeleted: boolean
  [propName: string]: any
}

export interface IEntityDataItemWithStatus extends IEntityDataItem {
  status: ISelectItem
}

/**
 * T without the keys K -- for an entity type, where Omit fails silently.
 * IEntityDataItem's index signature makes `keyof` of every entity type plain
 * `string | number`, so Omit<IEntity, 'x'> keeps only that signature and every
 * named field becomes `any`. This mapped type walks the declared keys, so the
 * rest stay typed.
 */
export type TOmitNamed<T, K extends PropertyKey> = {
  [P in keyof T as P extends K ? never : P]: T[P]
}

// Still Omit, so its fields are `any`: TOmitNamed exposes type errors in the
// mocks' Partial<entity> POST bodies (api/msw/api/orders).
export interface IEntityDataItemForPost extends Omit<IEntityDataItem, 'hrid'> {
  hrid?: THrid
}

export type TEntityData = IEntityDataItem | IEntityDataItem[]
export type TEntityDataForPost =
  | IEntityDataItemForPost
  | IEntityDataItemForPost[]

/**
 * A POST body that is not an entity: no stored row stands behind it, so there
 * is no id, hrid or version to send. A reply on a case's thread, an action
 * such as a resend.
 *
 * Not IOverviewForPost, which is for POSTing an Overview's shape
 * (IOrderOverviewForPost).
 *
 * `id?: never` is what makes this more than a label: an empty interface would
 * accept any object, an entity included.
 */
export interface INonEntityForPost {
  id?: never
}

export function isEntityDataItem(
  entityData: TEntityData
): entityData is IEntityDataItem {
  return (entityData as IEntityDataItem).hasOwnProperty('id')
}

export function isEntityDataItemArray(
  entityData: TEntityData
): entityData is IEntityDataItem[] {
  return (entityData as IEntityDataItem[]).constructor === Array
}

export type TPartialEntityData =
  | Partial<IEntityDataItem>
  | Partial<IEntityDataItem>[]

export function isPartialEntityDataItem(
  entityData: TPartialEntityData
): entityData is Partial<IEntityDataItem> {
  return (
    !(entityData as Partial<IEntityDataItem>).hasOwnProperty('id') &&
    (entityData as Partial<IEntityDataItem>[]).constructor !== Array
  )
}

export function isPartialEntityDataItemArray(
  entityData: TPartialEntityData
): entityData is Partial<IEntityDataItem>[] {
  return (entityData as Partial<IEntityDataItem>[]).constructor === Array
}

export interface IEntityDataGroup {
  title: string
  items: IEntityDataItem[]
}

export type TDeletedEntityData = string | string[]

export interface IEntityParentObjectForCreate {
  entityName: string
  object: IEntityDataItem
  // Optional silo association threaded through to the create dialog for contract
  // notes/documents: stamps the germane opportunity/order/company on the created
  // item (consumed by the hidden default fields in entityNote.ts / entityDocument.ts).
  siloEntityName?: 'opportunity' | 'order' | 'company'
  siloEntity?: IEntitySelectItem
  // Who a reply composed on this parent goes to by default -- for a case, the
  // correspondent who last wrote in (caseReplyRecipient). Read by the Send
  // Email form's To default.
  replyRecipient?: string
  // A Case Emails row's Reply all (replyAllRecipients): the whole To list --
  // the sender and the message's other To recipients -- which takes
  // precedence over replyRecipient, and the cc list, the message's cc's.
  replyRecipients?: string[]
  replyCcRecipients?: string[]
  // The inbound email a Case Emails row's Reply / Reply all answers, sent as
  // the reply's `inReplyToEmailId` (cynque-api-server #1814). Set only when
  // that row carries `readStatus` (replyPrefillForRow).
  inReplyToEmailId?: TEntityId
  // The Subject a reply composed on this parent starts with, where it answers
  // one particular message -- a Case Emails row's Reply To (replyPrefillForRow).
  // Unset, the Send Email form words its own from the parent's descr.
  replySubject?: string
  // The documents an email composed on this parent may attach -- for a case, its
  // visible documents (inline `cid:` parts excluded); for an order, opportunity,
  // company or supplier on its Activities tab, its documents. Read by the Send
  // Email form's Case Documents picker, the activity email's Attach <Record>
  // Documents picker, and their size/file-type checks.
  replyAttachableDocuments?: Array<{
    id: TEntityId
    filename: string
    filesize: number
  }>
  // The Document Type an Upload on this parent starts with -- Contract, when
  // the contract Sign check opened it (SiloOverview
  // requireContractDocumentToSign). Read by entityDocument.ts's default.
  documentType?: ISelectItem
}

export interface IEntityChildrenObject {
  entityName: string
  entityDataItems: IEntityDataItem[]
}

export interface IEntityParentChildrenObject {
  parent: IEntityParentObjectForCreate
  children: IEntityChildrenObject
}

export interface IName {
  prefix?: string
  firstName: string
  lastName: string
  suffix?: string
  descr?: string
  fullName?: string
  initials?: string
  lastPlusFirst?: string
}

export interface IAvatar {
  descr: string
  type: string
  src?: string
}

export interface IListGroup {
  title: string
  items: IEntityDataItem[]
}

export interface IHasIndexSignature {
  [key: string]: any
}

export type TTableCardHeaderLevel = 'PRIMARY' | 'SECONDARY' | 'TERTIARY'

export type TEnum = { [k: string]: number } // Could be tighter. JR.

// Typescript Utilities

export type PartialBy<T, K extends keyof T> = TOmitNamed<T, K> &
  Partial<Pick<T, K>>

// see https://dev.to/6akcuk/quick-tip-1-how-to-remove-an-index-signature-from-a-type-1eg5
export type RemoveIndexSignature<T> = {
  [K in keyof T as string extends K
    ? never
    : number extends K
    ? never
    : symbol extends K
    ? never
    : K]: T[K]
}

// A utility type that makes properties mutually exclusive
export type TSortFunctionResult = 0 | 1 | -1
export type XOR<T extends Record<string, any>> = {
  [K in keyof T]: {
    [P in K]: T[P]
  } & {
    [P in Exclude<keyof T, K>]?: never
  }
}[keyof T]

// export function assertIsNotUndefined<T>(val: T): asserts val is NonNullable<T> {
//   if (val === undefined) {
//     throw new TypeError('value is undefined or null.')
//   }
// }

export function isDefined(val: any) {
  return val !== undefined && val !== null
}

export function assertIsNotUndefined<T>(
  val: T,
  fn: string = '',
  descr: string = 'value'
): asserts val is NonNullable<T> {
  if (val === undefined) {
    throw new TypeError(`${fn} ${descr} is undefined.`)
  }
}

export function assertIsDefined<T>(
  val: T,
  fn: string = '',
  descr: string = 'value'
): asserts val is NonNullable<T> {
  if (val === undefined || val === null) {
    throw new Error(
      `${fn} ${descr} is ${val === undefined ? 'undefined' : 'null'}`
    )
  }
}

export function assert(condition: any, msg?: string): asserts condition {
  if (!condition) {
    throw new TypeError(msg)
  }
}

export function assertIsString(val: any): asserts val is string {
  if (typeof val !== 'string') {
    throw new TypeError('Not a string!')
  }
}

export function assertIsNumber(
  val: any,
  fn: string = '',
  descr: string = 'value'
): asserts val is number {
  if (typeof val !== 'number') {
    throw new TypeError(`${fn} ${descr} is not a number`)
  }
}

export function assertIsNumberAndIsGreaterThanZero(
  val: any,
  fn: string = '',
  descr: string = 'value'
): asserts val is number {
  assertIsNumber(val, fn, descr)
  if (val <= 0) {
    throw new TypeError(`${fn} ${descr} is less than or equal to 0: ${val}`)
  }
}

export function assertHasIdProperty(fn: string, val: any): asserts val is Ided {
  if (!hasIdProperty(val)) {
    console.error(`val:`, val)
    throw new Error(`${fn} val should be an object with an id, but it isn't.`)
  }
}

export function assertHasNumberIdProperty(
  fn: string,
  val: any
): asserts val is NumberIded {
  if (!hasNumberIdProperty(val)) {
    throw new Error(
      `${fn} thisField.value should have a number id property, but it doesn't.`
    )
  }
}

export function assertIsASelectItem(
  fn: string,
  val: any
): asserts val is ISelectItem {
  if (!isSelectItem(val)) {
    console.error(`val:`, val)
    throw new Error(`${fn} val should be an ISelectItem, but it's not.`)
  }
}

export function assertIsASelectItemWithEntityName(
  fn: string,
  val: any
): asserts val is ISelectItemWithEntityName {
  if (!isSelectItemWithEntityName(val)) {
    throw new Error(
      `${fn} val should be an ISelectItemWithEntityName, but it's not.`
    )
  }
}

export function assertIsAnEntitySelectItemAndHasAPopulatedParentProperty(
  fn: string,
  val: any
): asserts val is IEntitySelectItemWithParent {
  if (!isEntitySelectItem(val)) {
    throw new Error(`${fn} val should be an IEntitySelectItem, but it's not.`)
  }
  if (!('parent' in val)) {
    throw new Error(
      `${fn} val should be an IEntitySelectItem with` +
        ` a parent property, but it does not have a parent property.`
    )
  }
  if (!isSelectItem(val.parent)) {
    throw new Error(`${fn} val.parent should be an ISelectItem, but it's not.`)
  }
}

export function assertIsAnEntityDataItem(
  fn: string,
  val: any
): asserts val is IEntityDataItem {
  if (!isEntityDataItem(val)) {
    throw new Error(`${fn} val should be an IEntityDataItem, but it's not.`)
  }
}

export function assertIsAnEntityDataItemArray(
  fn: string,
  val: any
): asserts val is IEntityDataItem[] {
  if (!isEntityDataItemArray(val)) {
    throw new Error(
      `${fn} val should be an IEntityDataItemArray, but it's not.`
    )
  }
}

export function assertIsATEntityData(
  fn: string,
  val: any
): asserts val is TEntityData {
  if (!isEntityDataItem(val) && !isEntityDataItemArray(val)) {
    throw new Error(`${fn} val should be a TEntityData, but it's not.`)
  }
}

export function assertIsATPartialEntityData(
  fn: string,
  val: any
): asserts val is TEntityData {
  if (!isPartialEntityDataItem(val) && !isPartialEntityDataItemArray(val)) {
    throw new Error(`${fn} val should be a TPartialEntityData, but it's not.`)
  }
}

export function assertHasProperty<T extends object, K extends string>(
  fn: string,
  obj: T,
  propertyName: K,
  descr: string = 'obj'
): asserts obj is T & Record<K, unknown> {
  if (typeof obj !== 'object' || obj === null || !(propertyName in obj)) {
    throw new Error(
      `${fn} ${descr} should have a '${propertyName}' property, but it doesn't.`
    )
  }
}

export function assertIsAnEntityMaskData(
  fn: string,
  val: any
): asserts val is IEntityMaskData {
  if (!isEntityMaskData(val)) {
    throw new Error(`${fn} val should be an IEntityMaskData, but it's not.`)
  }
}

export type TPageToPushFunction = (id: string) => string

export function isOperativePageToPushFunction(
  val: any
): val is TPageToPushFunction {
  const fn = '[types/models.ts] isOperativePageToPushFunction |'
  const isOperativeFn = typeof val === 'function' && val.length === 1
  console.debug(`${fn} returning ${isOperativeFn}`)
  return isOperativeFn
}

export function isArrayOfStrings(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string')
}

interface IHasVersion {
  version: string
}

export function hasStringVersionProperty(obj: any): obj is IHasVersion {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    obj !== undefined &&
    'version' in obj &&
    typeof obj.version === 'string'
  )
}

export function assertIsArray(
  fn: string,
  val: unknown
): asserts val is unknown[] {
  if (!Array.isArray(val)) {
    throw new TypeError(`${fn} val should be an array, but isn't`)
  }
}

export function assertObjectIsNotAnArray(
  fn: string,
  val: unknown,
  descr: string = 'val'
): asserts val is object {
  if (Array.isArray(val)) {
    throw new TypeError(`${fn} ${descr} should NOT be an array, but is`)
  }
}

export function assertHasStringVersionProperty(
  fn: string,
  val: any
): asserts val is IHasVersion {
  if (!hasStringVersionProperty(val)) {
    throw new Error(
      `${fn} val should have a string 'version' property, but it doesn't.`
    )
  }
}

export function assertIsArrayAndAllObjectsHaveStringVersionProperty(
  fn: string,
  val: any
): asserts val is IHasVersion[] {
  if (!Array.isArray(val)) {
    throw new TypeError(`${fn} val should be an array, but isn't`)
  }
  if (!val.every(hasStringVersionProperty)) {
    throw new TypeError(`${fn} not all objects have a version property`)
  }
}
